import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Lock, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

const SetTeamMemberPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = 
      password.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    if (!isValid) {
      toast.error('Please meet all password requirements');
      return;
    }

    setLoading(true);
    try {
      // Use the v2 PostgreSQL endpoint
      const response = await axios.post(`${API_URL}/v2/verify-team-member`, {
        token,
        password
      });

      if (response.data.success) {
        toast.success('Password set successfully!');
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{background: '#f8f9fa'}}>
      <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
        <CardHeader className="text-center pt-16 pb-6">
          <div className="flex justify-center mb-4">
            <div className="p-4 rounded-xl shadow-lg" style={{background: '#2563EB'}}>
              <Lock className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            Create Your Password
          </CardTitle>
          <p className="text-gray-600 mt-2">
            Set a secure password for your account
          </p>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                Password *
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                className="h-12 text-base border-2 border-gray-200 focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
              
              <div className="flex items-center gap-2">
                {password.length >= 8 ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${password.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>8+ characters</span>
              </div>

              <div className="flex items-center gap-2">
                {/[a-z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${/[a-z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>1 lowercase letter</span>
              </div>

              <div className="flex items-center gap-2">
                {/[A-Z]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${/[A-Z]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>1 uppercase letter</span>
              </div>

              <div className="flex items-center gap-2">
                {/[0-9]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${/[0-9]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>1 number</span>
              </div>

              <div className="flex items-center gap-2">
                {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                <span className={`text-xs ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password) ? 'text-green-700' : 'text-gray-600'}`}>1 symbol (!@#$%^&*)</span>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 text-base font-semibold mt-6"
              style={{background: '#2563EB'}}
              disabled={loading || password.length < 8}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Setting Password...
                </span>
              ) : (
                "Set Password & Continue"
              )}
            </Button>
          </form>
        </CardContent>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © {new Date().getFullYear()} BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default SetTeamMemberPassword;
